import { FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { SidePanel } from '@carbon/ibm-products';
import {
  DatePicker,
  DatePickerInput,
  Dropdown,
  InlineNotification,
  TextInput,
} from '@carbon/react';
import type {
  IdentificationExtensionData,
  IdentificationResponse,
} from './types-identification';
import {
  editIdentificationAtom,
  identificationUrl,
} from './atoms/identification';
import {
  COLUMN_FIELDS,
  EXTENSION_FIELDS,
  IDENTIFICATION_FIELDS,
  fieldInputValue,
  type IdentificationFieldDef,
} from './identification-utils';
import { getDateString, parseUTCdateTime } from 'utils/dates';
import styles from './identification.module.scss';

// Edit panel for the AFE Identification section. Every field is driven by the
// shared IDENTIFICATION_FIELDS map (label / key / group / input), so the panel
// and the read-only table can never diverge. On save:
//   * column fields (group: 'column') submit FLAT under `identification`
//     (afe_number, afe_type, operator, afe_date, held_flag) — the BE
//     COLUMN_FIELDS whitelist.
//   * extension fields submit under `identification.extension_data`, keyed by
//     the SAME snake_case key used to read them (the service merges, not
//     replaces). Blank strings normalize to null.
// Request bodies stay snake_case; responses are read camelCase (KEEP).

interface Props {
  open: boolean;
  documentId: number | string;
  detail: IdentificationResponse;
  onClose: () => void;
  onSaved: () => void;
}

// Maps a `def.key` (camelCase column key) to its snake_case request key. The
// extension keys are already snake_case and round-trip verbatim.
const COLUMN_REQUEST_KEY: Record<string, string> = {
  afeNumber: 'afe_number',
  afeType: 'afe_type',
  afeDate: 'afe_date',
};

const blank = (value: string): string | null => {
  return value.trim() === '' ? null : value.trim();
};

export const IdentificationEditPanel: FC<Props> = ({
  open,
  documentId,
  detail,
  onClose,
  onSaved,
}) => {
  const editIdentification = useSetAtom(editIdentificationAtom);
  // Flat working copy keyed by def.key (string form for every input type).
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    const seeded: Record<string, string> = {};
    IDENTIFICATION_FIELDS.forEach((def) => {
      seeded[def.key] = fieldInputValue(
        def,
        detail.fields,
        detail.extensionData
      );
    });
    setValues(seeded);
  }, [open, detail]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setPending(true);
    setError(null);

    const identification: Record<string, unknown> = {};
    COLUMN_FIELDS.forEach((def) => {
      identification[COLUMN_REQUEST_KEY[def.key]] = blank(
        values[def.key] ?? ''
      );
    });

    const extensionData: IdentificationExtensionData = {};
    EXTENSION_FIELDS.forEach((def) => {
      extensionData[def.key] = blank(values[def.key] ?? '');
    });
    identification.extension_data = extensionData;

    editIdentification({
      url: identificationUrl(documentId),
      method: 'PATCH',
      data: { identification },
      onSuccess: () => {
        setPending(false);
        onSaved();
      },
      onError: (m) => {
        setPending(false);
        setError(m ?? 'Failed to save identification.');
      },
    });
  };

  const renderField = (def: IdentificationFieldDef) => {
    const id = `ident-${def.key}`;
    const value = values[def.key] ?? '';

    if (def.input === 'dropdown') {
      // Normalize options to {label, value}; preserve an existing value that is
      // not in the option set (e.g. an extractor-supplied Standardized AFE Type)
      // so the dropdown never silently clears it.
      const opts = (def.options ?? []).map((o) => {
        return typeof o === 'string' ? { label: o, value: o } : o;
      });
      if (value && !opts.some((o) => o.value === value)) {
        opts.push({ label: value, value });
      }
      return (
        <Dropdown
          key={def.key}
          id={id}
          light
          titleText={def.label}
          label="Choose an option"
          items={opts}
          itemToString={(item: { label: string; value: string } | null) => {
            return item ? item.label : '';
          }}
          selectedItem={opts.find((o) => o.value === value) ?? null}
          onChange={({
            selectedItem,
          }: {
            selectedItem: { label: string; value: string } | null;
          }) => setValue(def.key, selectedItem?.value ?? '')}
        />
      );
    }

    if (def.input === 'date') {
      return (
        <DatePicker
          key={def.key}
          datePickerType="single"
          // Submit ISO (YYYY-MM-DD via getDateString) so the backend casts the
          // date unambiguously — a US-format MM/DD/YYYY body gets day-first-cast
          // server-side (Jul 3 → Mar 7; Dec 25 → NULL). The working copy is
          // therefore ISO; flatpickr (Carbon's default m/d/Y format) can't parse
          // a bare ISO date, so feed the INPUT a MM/DD/YYYY display string
          // (parseUTCdateTime), while the picked Date rounds back to ISO.
          value={parseUTCdateTime(value)}
          onChange={(dates: Date[]) => {
            if (dates[0]) setValue(def.key, getDateString(dates[0]));
          }}
        >
          <DatePickerInput
            id={id}
            labelText={def.label}
            placeholder="MM/DD/YYYY"
            size="sm"
          />
        </DatePicker>
      );
    }

    return (
      <TextInput
        key={def.key}
        id={id}
        light
        labelText={def.label}
        size="sm"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue(def.key, e.target.value)
        }
      />
    );
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Edit AFE Identification"
      subtitle="Update any field below and click Save to apply your changes."
      slideIn
      selectorPageContent=".afe-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save',
          disabled: pending,
          onClick: handleSave,
        },
      ]}
    >
      <div className={`${styles.panelForm} ${styles.panelLightFields}`}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        {/* Single column, matching the v2 mock's AFE edit drawer
            (.layout2__afe-panel-form is flex-direction: column). */}
        <div className={styles.panelColumn}>
          {IDENTIFICATION_FIELDS.map(renderField)}
        </div>
      </div>
    </SidePanel>
  );
};
